"use client"

import { forwardRef, useMemo, useRef } from "react"
import { ChatHistoryItem } from "./ChatHistoryItem"
import { useRouter } from "next/router"
import { Virtuoso, VirtuosoHandle } from "react-virtuoso"
import ChatLoading from "../ChatLoading"
import useInfiniteConversationsQuery from "@/queries/ai-agent/useInfiniteConversationsQuery"
import IconButton from "@/components/IconButton"
import { CloseIcon } from "@/components/shared/icons"
import { cn } from "@/lib/utils"

const Item = forwardRef<HTMLDivElement, { children?: React.ReactNode; className?: string }>(
  ({ children, className, ...props }, ref) => (
    <div {...props} ref={ref} className={cn("virtuoso-item p-1", className)}>
      {children}
    </div>
  ),
)

const List = forwardRef<HTMLDivElement, { children?: React.ReactNode; className?: string }>(
  ({ children, className, ...props }, ref) => (
    <div {...props} ref={ref} className={cn("p-2", className)}>
      {children}
    </div>
  ),
)

export const ChatHistory = ({
  setIsOpenSidebar,
  className,
}: {
  setIsOpenSidebar: (isOpen: boolean) => void
  className?: string
}) => {
  const router = useRouter()
  const currentRoomId = router.query["conversation-id"] as string

  const virtuosoRef = useRef<VirtuosoHandle>(null)
  const {
    data: conversations,
    isLoading: isLoadingConversations,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteConversationsQuery()

  // Pre-process conversations to create grouped items with headers
  const processedItems = useMemo(() => {
    if (!conversations || conversations.length === 0) return []

    const currentTime = new Date().getTime() / 1000
    const cutoffTime = currentTime - 60 * 60 * 24 * 3 // 3 days

    let hasShownRecent = false
    let hasShownPrevious = false

    const result: Array<{
      type: "header" | "item"
      label?: string
      item?: (typeof conversations)[0]
    }> = []

    conversations.forEach(item => {
      const timestamp = Number(item.createdAt)
      const isRecent = timestamp >= cutoffTime

      // Add Recent header if needed
      if (isRecent && !hasShownRecent) {
        result.push({ type: "header", label: "Recent" })
        hasShownRecent = true
      }

      // Add Previous header if needed
      if (!isRecent && !hasShownPrevious) {
        result.push({ type: "header", label: "Previous" })
        hasShownPrevious = true
      }

      // Add the item
      result.push({ type: "item", item })
    })

    return result
  }, [conversations])

  return (
    <div className={cn("flex flex-col h-full overflow-hidden", className)}>
      {/* Header - Fixed height */}
      <div className="flex justify-between w-full px-3 py-2 border-b border-atherGray-700">
        <h5 className="text-xl font-bold text-md text-atherGray-300 w-fit">Chat History</h5>
        <IconButton
          colorScheme="secondary"
          className="!p-2 min-h-auto rounded-full"
          onClick={() => {
            setIsOpenSidebar(false)
          }}
        >
          <CloseIcon width={14} height={14} />
        </IconButton>
      </div>
      {/* Scrollable content area */}
      <div className="flex-1">
        <Virtuoso
          ref={virtuosoRef}
          data={processedItems}
          className="overflow-hidden overflow-y-auto virtuoso-chat-scroll"
          defaultItemHeight={40}
          computeItemKey={index => `item-${index}`}
          endReached={() => {
            if (hasNextPage) {
              fetchNextPage()
            }
          }}
          itemContent={(_, item) => {
            if (item.type === "header") {
              return <p className="px-1 py-2 text-sm font-bold text-atherGray-500">{item.label}</p>
            }

            if (item.type === "item" && item.item) {
              return (
                <ChatHistoryItem
                  onClick={() => {
                    setIsOpenSidebar(false)
                  }}
                  key={item.item.sessionId}
                  id={item.item.sessionId}
                  title={item.item.title}
                  timestamp={item.item.createdAt}
                  isActive={currentRoomId === item.item.sessionId}
                />
              )
            }

            return null
          }}
          components={{
            Footer: () => (hasNextPage ? <ChatLoading /> : null),
            Item,
            List,
          }}
        />
      </div>
    </div>
  )
}
